import { css, useTheme } from "@emotion/react"
import styled from "@emotion/styled"
import { Link } from "gatsby"
import { StaticImage } from "gatsby-plugin-image"
import React, { useEffect, useRef } from "react"
import close from "../../images/layout/close.png"
import menu from "../../images/layout/menu.png"
import bgBig from "../../images/layout/mobile-nav-big.jpg"
import bg from "../../images/layout/mobile-nav.jpg"
import { BlackButton, BlueButton, Button } from "./ReusableComponents"

const Header = styled.header`
  background: ${({ theme }) => theme.white};
  padding: 16px 23px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 auto;
  max-width: 1600px;

  @media (min-width: 1360px) {
    padding: 21px 23px;
  }
`

const Hamburger = styled(Button)`
  height: 34px;
  width: 32px;
  background: ${({ mobileNav }) => `url(${mobileNav ? close : menu})`};
  transition: background 0.2s ease-in;

  @media (min-width: 1360px) {
    display: none;
  }
`

const Menu = styled.div`
  position: fixed;
  left: ${({ mobileNav }) => (mobileNav ? 0 : "150vw")};
  top: 72px;
  width: 100%;
  height: calc(100% - 72px);
  overflow-y: auto;
  transition: left 0.3s;
  z-index: 1000;
  padding: 29px 16px;
  background: linear-gradient(
      0deg,
      rgba(255, 255, 255, 0.9),
      rgba(255, 255, 255, 0.9)
    ),
    url(${bg});
  background-size: cover;
  background-position: center;
  backdrop-filter: blur(16px);
  display: flex;
  align-items: center;
  flex-direction: column;

  > :last-child {
    margin-bottom: 0;
  }

  @media (min-height: 650px) and (max-width: 1332px) {
    padding: 41px 16px;
  }

  @media (min-width: 768px) {
    background: linear-gradient(
        0deg,
        rgba(255, 255, 255, 0.9),
        rgba(255, 255, 255, 0.9)
      ),
      url(${bgBig});
    background-size: cover;
    background-position: center top;
  }

  @media (min-width: 1360px) {
    position: absolute;
    flex-direction: row;
    justify-content: space-between;
    left: 0;
    right: 0;
    top: 92px;
    height: max-content;
    background: rgba(242, 242, 242, 0.5);
    backdrop-filter: blur(10px);
    padding: 16px;

    @supports not ((-webkit-backdrop-filter: none) or (backdrop-filter: none)) {
      background: rgba(242, 242, 242, 0.7);
    }
  }

  @media (min-width: 1600px) {
    padding: 16px calc((100vw - 1577px) / 2);
  }
`

const NavBtn = styled(BlackButton)`
  position: relative;
  margin-bottom: 29px;
  text-transform: uppercase;

  ::after {
    content: "";
    height: 6px;
    width: 6px;
    display: inline-block;
    border-left: 1px solid black;
    border-bottom: 1px solid black;
    transform: rotate(-45deg) translateY(-3px);
    margin-left: 13px;
    margin-bottom: 2px;
  }

  @media (min-height: 650px) {
    margin-bottom: 41px;
  }

  @media (min-width: 1360px) {
    margin-bottom: 0;
  }
`

const SubLink = styled(Link)`
  color: ${({ theme }) => theme.dark};
  text-transform: uppercase;

  margin-bottom: 29px;

  @media (min-height: 650px) {
    margin-bottom: 41px;
  }

  @media (min-width: 1360px) {
    margin-bottom: 0;
  }
`

const DesktopSubNav = styled.div`
  display: none;

  @media (min-width: 1360px) {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    left: 0;
    right: 0;
    top: ${({ subMenu }) => (subMenu ? "161px" : "-300px")};
    background: rgba(255, 255, 255, 0.62);
    padding: 8px;
    width: 100%;
    z-index: 10000;
    transition: top 0.3s ease-in;
  }
`

const links = [
  {
    text: "dental implants",
    link: "/services/dental-implants/",
    subMenu: false,
  },
  {
    text: "cosmetic dentistry",
    subMenu: true,
  },
  {
    text: "periodontal",
    link: "/services/periodontal-therapy/",
    subMenu: false,
  },
  {
    text: "orthodontics",
    subMenu: true,
  },

  {
    text: "procedures",
    subMenu: true,
  },
  {
    text: "about us",
    subMenu: true,
  },
  {
    text: "blog",
    link: "/blog/",
    subMenu: false,
  },

  {
    text: "contact us",
    link: "/contact-us/",
    subMenu: false,
  },
]

const subLinks = {
  "cosmetic dentistry": [
    {
      text: "porcelain veneers",
      link: "/services/porcelain-veneers/",
    },
    {
      text: "tooth colored restoration",
      link: "/services/tooth-colored-fillings/",
    },
    {
      text: "dental crowns",
      link: "/services/dental-crowns/",
    },
    {
      text: "same day crowns",
      link: "/services/same-day-crowns/",
    },
    {
      text: "teeth whitening",
      link: "/services/teeth-whitening/",
    },
  ],
  orthodontics: [
    {
      text: "invisalign",
      link: "/services/invisible-orthodontics/",
    },
  ],

  procedures: [
    {
      text: "sedation dentistry",
      link: "/services/sedation-dentistry/",
    },
    {
      text: "laser dentistry",
      link: "/services/laser-dentistry/",
    },
    {
      text: "sleep apnea",
      link: "/services/sleep-apnea/",
    },
  ],

  "about us": [
    {
      text: "about us",
      link: "/about-us/",
    },
    {
      text: "our team",
      link: "/about-us/our-team/",
    },
    {
      text: "about doctor",
      link: "/about-us/about-doctor/",
    },

    {
      text: "cherry payment plans",
      link: "/about-us/cherry-payment-plans/",
    },
  ],
}

export default function Nav({ mobileNav, setMobileNav }) {
  const [subMenu, setSubMenu] = React.useState("")
  const theme = useTheme()

  const subNavRef = useRef()
  const menuRef = useRef()

  const closeSubNav = e => {
    if (
      !subNavRef.current.contains(e.target) &&
      !menuRef.current.contains(e.target)
    )
      setSubMenu("")
  }

  const closeSubNavKey = e => {
    if (e.key === "Esc" || e.key === "Escape") setSubMenu("")
  }

  useEffect(() => {
    document.addEventListener("mousedown", closeSubNav)
    document.addEventListener("keydown", closeSubNavKey)
    return () => {
      document.removeEventListener("mousedown", closeSubNav)
      document.removeEventListener("keydown", closeSubNavKey)
    }
  }, [])

  return (
    <section
      css={css`
        position: relative;
      `}
    >
      <Header>
        <BlueButton
          as="a"
          href="tel:+17274416060"
          css={css`
            font-weight: 700;
            display: none;

            @media (min-width: 1360px) {
              display: block;
            }
          `}
        >
          727-441-6060
        </BlueButton>

        <Link to="/">
          <StaticImage
            src="../../images/layout/logo.png"
            alt="chamberlain dental"
            width={239}
            height={40}
            loading="eager"
            placeholder="none"
          />
        </Link>

        <p
          css={css`
            font-weight: 700;
            color: ${theme.blue};
            margin: 0;
            display: none;

            @media (min-width: 1360px) {
              display: block;
            }
          `}
        >
          555 S Hercules Avenue #403
          <br />
          Clearwater, FL 33764
        </p>

        <Hamburger
          mobileNav={mobileNav}
          onClick={() => {
            setMobileNav(nav => !nav)
            setSubMenu("")
          }}
          title={mobileNav ? "close menu" : "open menu"}
        />
      </Header>
      <Menu mobileNav={mobileNav} ref={menuRef}>
        {links.map((link, i) =>
          link.subMenu ? (
            <NavBtn
              key={i}
              onClick={() =>
                setSubMenu(current => (current === link.text ? "" : link.text))
              }
              css={css`
                background: ${subMenu === link.text ? "#f2f2f2" : "none"};
                font-weight: 500;
                box-shadow: ${subMenu === link.text
                  ? `inset 0 0 0 2px ${theme.dark}`
                  : ""};

                display: ${subMenu ? "none" : "block"};

                ::after {
                  transform: ${subMenu === link.text
                    ? "rotate(135deg) translateY(0)"
                    : "rotate(-45deg) translateY(-3px)"};
                }

                @media (min-width: 1360px) {
                  display: block;
                }
              `}
            >
              {link.text}
            </NavBtn>
          ) : (
            <BlackButton
              as={Link}
              to={link.link}
              key={i}
              css={css`
                text-transform: uppercase;
                margin-bottom: 29px;
                display: ${subMenu ? "none" : "block"};
                font-weight: 500;
                @media (min-height: 650px) {
                  margin-bottom: 41px;
                }

                @media (min-width: 1360px) {
                  display: block;
                  margin-bottom: 0;
                }
              `}
            >
              {link.text}
            </BlackButton>
          )
        )}
        {subMenu ? (
          <>
            <NavBtn
              onClick={() => setSubMenu("")}
              css={css`
                background: #f2f2f2;

                ::after {
                  transform: rotate(135deg) translateY(0);
                }

                @media (min-width: 1360px) {
                  display: none;
                }
              `}
            >
              {subMenu}
            </NavBtn>
            {subLinks[subMenu].map((subLink, i) => (
              <SubLink
                to={subLink.link}
                key={i}
                css={css`
                  :last-of-type {
                    margin-bottom: 0;
                  }

                  @media (min-width: 1360px) {
                    display: none;
                  }
                `}
              >
                {subLink.text}
              </SubLink>
            ))}
            <BlackButton
              onClick={() => setSubMenu("")}
              css={css`
                margin-top: auto;
                border: none;
                font-weight: 700;

                @media (min-width: 1360px) {
                  display: none;
                }
              `}
            >
              Back to main menu
            </BlackButton>
          </>
        ) : (
          ""
        )}
      </Menu>

      <DesktopSubNav subMenu={subMenu} ref={subNavRef}>
        {subMenu &&
          subLinks[subMenu].map((link, i) => (
            <BlackButton
              as={Link}
              to={link.link}
              key={i}
              css={css`
                text-transform: uppercase;
                margin-right: 64px;

                :last-of-type {
                  margin-right: 0;
                }

                @media (max-width: 1375px) {
                  margin-right: 56px;
                }
              `}
            >
              {link.text}
            </BlackButton>
          ))}
      </DesktopSubNav>
    </section>
  )
}
